import React, { useEffect, useMemo, useRef } from 'react';
import {
  Pencil,
  Trash2,
  Dog,
  BookOpen,
  ArrowRight,
} from 'lucide-react';
import { parseISO } from 'date-fns';
import { Customer, Booking } from '../types';
import { Avatar, Badge, Card, IconButton } from './ui';

interface BookingListViewProps {
  customers: Customer[];
  searchTerm: string;
  onEditBooking: (booking: Booking) => void;
  onDeleteBooking: (id: string) => void;
}

interface BookingWithGuest extends Booking {
  guestName: string;
  guestEmail: string;
}

const fmtDate = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const fmtDateShort = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
};

const findFocusBooking = <T extends Pick<Booking, 'fromDate' | 'toDate'>>(
  list: T[]
): T | undefined => {
  const now = new Date();
  const current = list.find((b) => {
    const from = parseISO(b.fromDate);
    const to = parseISO(b.toDate);
    return from <= now && now <= to;
  });
  if (current) return current;
  return list.find((b) => parseISO(b.fromDate) > now);
};

const BookingListView: React.FC<BookingListViewProps> = ({
  customers,
  searchTerm,
  onEditBooking,
  onDeleteBooking,
}) => {
  // Chronological — earliest first. Current/next is highlighted and
  // auto-scrolled into view on mobile.
  const allBookings = useMemo<BookingWithGuest[]>(
    () =>
      customers
        .flatMap((customer) =>
          customer.bookings.map((booking) => ({
            ...booking,
            guestName: customer.name,
            guestEmail: customer.email,
          }))
        )
        .filter(
          (b) =>
            b.guestName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (b.notes &&
              b.notes.toLowerCase().includes(searchTerm.toLowerCase()))
        )
        .sort(
          (a, b) =>
            new Date(a.fromDate).getTime() - new Date(b.fromDate).getTime()
        ),
    [customers, searchTerm]
  );

  const focusBooking = useMemo(
    () => findFocusBooking(allBookings),
    [allBookings]
  );
  const focusId = focusBooking?.id;

  // Scroll the current/next stay into view on first render of a non-empty list.
  const containerRef = useRef<HTMLUListElement>(null);
  const initialisedRef = useRef(false);
  useEffect(() => {
    if (initialisedRef.current) return;
    if (!focusId || !containerRef.current) return;
    const el = containerRef.current.querySelector<HTMLElement>(
      `[data-booking-id="${focusId}"]`
    );
    if (el) {
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
      initialisedRef.current = true;
    }
  }, [focusId]);

  return (
    <div className="p-3 md:p-6">
      <Card className="overflow-hidden">
        <div className="hidden md:grid md:grid-cols-[1.4fr_1.4fr_0.8fr_0.9fr_1fr_0.7fr] items-center px-5 py-3 border-b border-sand-200 bg-sand-50/60 text-[11px] font-medium text-sand-600 uppercase tracking-wider">
          <span>Guest</span>
          <span>Stay</span>
          <span>Party</span>
          <span>Net</span>
          <span>Welcome tray</span>
          <span className="text-right">Actions</span>
        </div>

        <ul ref={containerRef} className="divide-y divide-sand-100">
          {allBookings.map((b) => {
            const isFocus = focusId === b.id;
            return (
              <li
                key={b.id}
                data-booking-id={b.id}
                className={[
                  'group transition-colors',
                  isFocus ? 'bg-sage-50/60' : 'hover:bg-sand-50',
                ].join(' ')}
              >
                {/* Desktop row */}
                <div className="hidden md:grid md:grid-cols-[1.4fr_1.4fr_0.8fr_0.9fr_1fr_0.7fr] md:items-center gap-2 px-5 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar name={b.guestName} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-sand-900 truncate">
                        {b.guestName}
                        {isFocus && (
                          <span className="ml-2 text-[10px] font-medium text-sage-700 bg-sage-100 px-1.5 py-0.5 rounded">
                            Now
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-sand-500 truncate">
                        {b.guestEmail}
                      </p>
                    </div>
                  </div>
                  <div className="text-sm text-sand-800 flex items-center gap-1.5">
                    {fmtDate(b.fromDate)}
                    <ArrowRight className="h-3 w-3 text-sand-400" />
                    {fmtDate(b.toDate)}
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="text-sm text-sand-700">
                      {b.adults}A · {b.children}C · {b.infants}I
                    </span>
                    {b.dogs > 0 && (
                      <Badge tone="sage" className="ml-1">
                        <Dog className="h-3 w-3 mr-1 inline" />
                        {b.dogs}
                      </Badge>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-sage-700">
                      £{Number(b.ourIncome || 0).toLocaleString('en-GB')}
                    </p>
                    <p className="text-[11px] text-sand-500">
                      Gross £{Number(b.amountPaid).toLocaleString('en-GB')}
                    </p>
                  </div>
                  <div
                    className="text-sm text-sand-600 truncate"
                    title={b.welcomeTrayContents || ''}
                  >
                    {b.welcomeTrayContents || (
                      <span className="text-sand-400">—</span>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <IconButton
                      label="Edit booking"
                      onClick={() => onEditBooking(b)}
                    >
                      <Pencil className="h-4 w-4" />
                    </IconButton>
                    <IconButton
                      label="Delete booking"
                      variant="danger"
                      onClick={() => onDeleteBooking(b.id!)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </IconButton>
                  </div>
                </div>

                {/* Mobile card */}
                <div className="md:hidden px-4 py-3">
                  <div className="flex items-start gap-3">
                    <Avatar name={b.guestName} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <p className="text-sm font-medium text-sand-900 truncate">
                          {b.guestName}
                          {isFocus && (
                            <span className="ml-2 text-[10px] font-medium text-sage-700 bg-sage-100 px-1.5 py-0.5 rounded">
                              Now
                            </span>
                          )}
                        </p>
                        <span className="text-sm font-medium text-sage-700 shrink-0">
                          £{Number(b.ourIncome || 0).toLocaleString('en-GB')}
                        </span>
                      </div>
                      <p className="text-xs text-sand-700 flex items-center gap-1.5">
                        {fmtDateShort(b.fromDate)}
                        <ArrowRight className="h-3 w-3 text-sand-400" />
                        {fmtDateShort(b.toDate)}
                      </p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        <Badge tone="sand">
                          {b.adults}A · {b.children}C · {b.infants}I
                        </Badge>
                        {b.dogs > 0 && (
                          <Badge tone="sage">
                            <Dog className="h-3 w-3 mr-0.5 inline" />
                            {b.dogs}
                          </Badge>
                        )}
                        {b.checkoutIssues && (
                          <Badge tone="red">Issue</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-1 mt-2">
                    <IconButton
                      label="Edit booking"
                      onClick={() => onEditBooking(b)}
                    >
                      <Pencil className="h-4 w-4" />
                    </IconButton>
                    <IconButton
                      label="Delete booking"
                      variant="danger"
                      onClick={() => onDeleteBooking(b.id!)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </IconButton>
                  </div>
                </div>

                {b.checkoutIssues && (
                  <div className="hidden md:block px-5 pb-3">
                    <div className="ml-12 inline-flex items-center gap-2 bg-red-50 text-red-700 text-xs rounded-lg px-2.5 py-1">
                      <span className="font-medium">Issue:</span>
                      <span>{b.checkoutIssues}</span>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {allBookings.length === 0 && (
          <div className="py-12 md:py-16 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-sand-100 text-sand-500 mb-3">
              <BookOpen className="h-5 w-5" />
            </div>
            <p className="text-sand-700 font-medium">No bookings found</p>
            <p className="text-sand-500 text-sm mt-1">
              Add a booking to get started.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default BookingListView;
